/**
 * Formulas with the chat composer's native textarea and signal tags. One editor
 * for the Number chart (`dialect="number"`) and derived signals
 * (`dialect="derived"`); the dialect decides the grammar and the wording.
 */
import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../lit-base.ts';
import type { ColumnInfo } from '../api/types.ts';
import {
  applyNumberFormulaSuggestion, numberFormulaCompletion, numberFormulaSuggestions,
  numberFormulaSegments, validateNumberFormula, DERIVED_DIALECT, NUMBER_DIALECT,
  type FormulaCompletion, type FormulaSuggestion,
} from '../features/plot/number-formula.ts';

const COPY = {
  number: {
    placeholder: 'Start with SUM, AVG… or type @ for a signal',
    help: 'Type a summary function, then choose a signal. Use @ to search signals. Enter or Tab accepts a suggestion; Ctrl+Enter plots.',
    idle: 'Choose a function to start.',
    ok: '✓ Ready to plot',
    choosingSignal: 'Choose a signal to complete the summary.',
  },
  derived: {
    placeholder: 'e.g. "Fuel rate" / "STW", or "STW" - AVG("STW", 1h)',
    help: 'Type a signal or a function; Enter or Tab accepts a suggestion. AVG("STW", 1h) is the trailing-hour average at every row; AVG("STW") is one average over the selected range. Windows: s, m, h, d.',
    idle: 'Choose a signal to start.',
    ok: '✓ Valid',
    choosingSignal: 'Choose a signal.',
  },
} as const;

let nextId = 0;

@customElement('number-formula-editor')
export class NumberFormulaEditor extends LightElement {
  @property() value = '';
  @property({ attribute: false }) columns: ColumnInfo[] = [];
  @property({ attribute: false }) change: (value: string) => void = () => {};
  @property() dialect: 'number' | 'derived' = 'number';
  @property() label = 'Formula';
  @state() private menu: FormulaSuggestion[] = [];
  @state() private menuIndex = 0;
  private completion: FormulaCompletion | null = null;
  private inputId = `number-formula-${++nextId}`;
  private menuId = `${this.inputId}-suggestions`;
  private known = (name: string): boolean => this.columns.some((col) => col.name === name);
  private get grammar() { return this.dialect === 'derived' ? DERIVED_DIALECT : NUMBER_DIALECT; }

  private refreshMenu(area: HTMLTextAreaElement): void {
    this.completion = numberFormulaCompletion(area.value, area.selectionStart, this.grammar);
    this.menu = this.completion
      ? numberFormulaSuggestions(this.completion, this.columns.map((col) => col.name), this.grammar) : [];
    this.menuIndex = 0;
  }

  private commit(value: string): void {
    this.value = value;
    this.change(value);
  }

  private closeMenu(): void { this.menu = []; this.completion = null; }

  private pick(item: FormulaSuggestion): void {
    if (!this.completion) return;
    const next = applyNumberFormulaSuggestion(this.value, this.completion, item, this.grammar);
    if (next.text.length > 1000) return;
    this.commit(next.text);
    this.closeMenu();
    void this.updateComplete.then(() => {
      const area = this.querySelector('textarea');
      if (!area) return;
      area.focus(); area.setSelectionRange(next.caret, next.caret);
      this.syncHighlightScroll(area);
      if (item.kind === 'function') this.refreshMenu(area);
    });
  }

  private insertOperator(operator: string): void {
    const area = this.querySelector('textarea');
    if (!area) return;
    const start = area.selectionStart, end = area.selectionEnd;
    const insert = ['(', ')'].includes(operator) ? operator : ` ${operator} `;
    const next = this.value.slice(0, start) + insert + this.value.slice(end);
    if (next.length > 1000) return;
    this.commit(next);
    void this.updateComplete.then(() => {
      area.focus(); area.setSelectionRange(start + insert.length, start + insert.length);
      this.syncHighlightScroll(area); this.refreshMenu(area);
    });
  }

  private onKeydown(e: KeyboardEvent): void {
    if (e.isComposing || e.ctrlKey || e.metaKey || e.altKey) return;
    if (this.menu.length) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        this.menuIndex = (this.menuIndex + (e.key === 'ArrowDown' ? 1 : -1) + this.menu.length) % this.menu.length;
        void this.updateComplete.then(() => {
          const item = this.querySelector<HTMLElement>('.number-formula-menu .active');
          const menu = this.querySelector<HTMLElement>('.number-formula-menu');
          if (item && menu) {
            if (item.offsetTop < menu.scrollTop) menu.scrollTop = item.offsetTop;
            else if (item.offsetTop + item.offsetHeight > menu.scrollTop + menu.clientHeight) {
              menu.scrollTop = item.offsetTop + item.offsetHeight - menu.clientHeight;
            }
          }
        });
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault(); const item = this.menu[this.menuIndex]; if (item) this.pick(item); return;
      }
      if (e.key === 'Escape') { e.preventDefault(); this.closeMenu(); return; }
    }
    if (e.key.startsWith('Arrow') || e.key === 'Home' || e.key === 'End') {
      requestAnimationFrame(() => this.refreshMenu(e.target as HTMLTextAreaElement));
    }
  }

  private syncHighlightScroll(area: HTMLTextAreaElement): void {
    const layer = this.querySelector<HTMLElement>('.assistant-highlight');
    if (layer) { layer.scrollTop = area.scrollTop; layer.scrollLeft = area.scrollLeft; }
  }

  override render(): TemplateResult {
    const error = validateNumberFormula(this.value, this.known, this.grammar);
    const segments = numberFormulaSegments(this.value, this.known, this.grammar);
    const copy = COPY[this.dialect];
    const choosing = this.menu.length > 0;
    return html`
      <div class="expr-input-block number-formula">
        <label for=${this.inputId}>${this.label}</label>
        <div class="assistant-input-stack number-formula-stack">
          <div class="assistant-highlight" aria-hidden="true">${segments.map((part) =>
            part.kind ? html`<span class="assistant-tagged">${part.text}</span>` : part.text)}<span class="assistant-highlight-tail"></span></div>
          <textarea id=${this.inputId} class="assistant-input" rows="3" maxlength="1000"
            placeholder=${copy.placeholder}
            aria-label=${this.dialect === 'derived' ? 'Derived signal formula' : 'Number formula'} aria-autocomplete="list" aria-controls=${this.menuId}
            aria-expanded=${choosing} aria-invalid=${!choosing && !!this.value.trim() && !!error}
            aria-describedby=${`${this.inputId}-help ${this.inputId}-status`}
            aria-activedescendant=${this.menu.length ? `${this.menuId}-${this.menuIndex}` : nothing}
            autocomplete="off" spellcheck="false" .value=${this.value}
            @input=${(e: Event) => {
              const area = e.target as HTMLTextAreaElement;
              this.commit(area.value); this.refreshMenu(area);
            }} @focus=${(e: Event) => this.refreshMenu(e.target as HTMLTextAreaElement)}
            @click=${(e: Event) => this.refreshMenu(e.target as HTMLTextAreaElement)}
            @blur=${() => this.closeMenu()} @keydown=${this.onKeydown}
            @scroll=${(e: Event) => this.syncHighlightScroll(e.target as HTMLTextAreaElement)}></textarea>
        </div>
        ${this.menu.length ? html`<div class="combobox-menu number-formula-menu" id=${this.menuId} role="listbox" aria-label="Formula suggestions">
          ${this.menu.map((item, i) => html`<div class="combobox-item ${i === this.menuIndex ? 'active' : ''}"
            id=${`${this.menuId}-${i}`} role="option" aria-selected=${i === this.menuIndex}
            @mousedown=${(e: Event) => { e.preventDefault(); this.pick(item); }}
            @mouseenter=${() => { this.menuIndex = i; }}>
            <span class="assistant-mention-kind">${item.kind === 'signal' ? 'Signal' : item.name}</span>
            <span>${item.label}</span>
          </div>`)}
        </div>` : nothing}
        <div class="seg number-formula-operators" role="group" aria-label="Insert formula operation">
          ${[['+', '+'], ['-', '−'], ['*', '×'], ['/', '÷'], ['(', '('], [')', ')']].map(([operator, label]) => html`
            <button type="button" class="seg-btn" title=${`Insert ${label}`}
              @mousedown=${(e: Event) => e.preventDefault()} @click=${() => this.insertOperator(operator!)}>${label}</button>`)}
        </div>
        <div class="toggle-hint" id=${`${this.inputId}-help`}>${copy.help}</div>
        <div class="expr-status ${!choosing && this.value.trim() ? error ? 'is-error' : 'is-ok' : ''}" id=${`${this.inputId}-status`} aria-live="polite">
          ${choosing ? this.completion?.kind === 'signal' ? copy.choosingSignal
            : this.dialect === 'derived' ? 'Choose a signal or a function.' : 'Choose a summary function.'
            : this.value.trim() ? error ? `✗ ${error}` : copy.ok : copy.idle}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'number-formula-editor': NumberFormulaEditor; }
}
